import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import client from "../../../api/camasirhaneApi";

const HEADER_FILL = "FF2C3E50";
const HEADER_FONT = "FFFFFFFF";
function styleHeaderRow(row) {
  row.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.font = { bold: true, color: { argb: HEADER_FONT } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  });
}

function addSheet(workbook, name, headers, rows, numCols = headers.length) {
  const ws = workbook.addWorksheet(name);
  ws.addRow(headers);
  styleHeaderRow(ws.getRow(1));
  headers.forEach((_, i) => {
    ws.getColumn(i + 1).width = i === 0 ? 22 : 16;
  });
  rows.forEach((row) => ws.addRow(row));
  ws.views = [{ state: "frozen", ySplit: 1 }];
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } };
  for (let c = 2; c <= numCols; c += 1) {
    ws.getColumn(c).numFmt = "#,##0.0";
  }
  return ws;
}

async function fetchList(path, params) {
  const res = await client.get(path, { params });
  const data = res.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

async function fetchMaterialSummaryFromRecords(year, months) {
  const [hotels, departments, materials] = await Promise.all([
    fetchList("/hotels/"),
    fetchList("/departments/"),
    fetchList("/materials/"),
  ]);

  const hotelMap = new Map(hotels.map((h) => [h.id, h.name]));
  const deptMap = new Map(departments.map((d) => [d.id, d.name]));
  const matMap = new Map(materials.map((m) => [m.id, m.name]));

  const weighingMap = new Map();
  const productionMap = new Map();

  await Promise.all(
    months.map(async (month) => {
      const [weighing, production] = await Promise.all([
        fetchList("/weighing-records/", { params: { year, month } }),
        fetchList("/production-records/", { params: { year, month } }),
      ]);

      weighing.forEach((rec) => {
        const hotel = hotelMap.get(rec.hotel_id) || "?";
        const dept = deptMap.get(rec.department_id) || "?";
        const material = matMap.get(rec.material_id) || "?";
        const key = `${hotel}|${dept}|${material}|${rec.weighing_type}`;
        weighingMap.set(key, (weighingMap.get(key) || 0) + Number(rec.weight_kg || 0));
      });

      production.forEach((rec) => {
        const hotel = hotelMap.get(rec.hotel_id) || "?";
        const material = matMap.get(rec.material_id) || "?";
        const key = `${hotel}|${material}`;
        const cur = productionMap.get(key) || { qty: 0, unit: Number(rec.unit_weight_kg) || 0, kg: 0 };
        cur.qty += Number(rec.quantity) || 0;
        cur.kg += Number(rec.total_weight_kg) || Number(rec.quantity) * Number(rec.unit_weight_kg) || 0;
        if (rec.unit_weight_kg) cur.unit = Number(rec.unit_weight_kg);
        productionMap.set(key, cur);
      });
    })
  );

  if (!weighingMap.size && !productionMap.size) return null;

  const weighing = [...weighingMap.entries()]
    .map(([key, total_kg]) => {
      const [hotel, department, material, type] = key.split("|");
      return { hotel, department, material, type, total_kg };
    })
    .sort((a, b) => {
      if (a.hotel !== b.hotel) return a.hotel.localeCompare(b.hotel, "tr");
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      if (a.department !== b.department) return a.department.localeCompare(b.department, "tr");
      return a.material.localeCompare(b.material, "tr");
    });

  const production = [...productionMap.entries()]
    .map(([key, item]) => {
      const [hotel, material] = key.split("|");
      return [hotel, material, item.qty || null, item.unit || null, item.kg];
    })
    .sort((a, b) => a[0].localeCompare(b[0], "tr") || a[1].localeCompare(b[1], "tr"));

  return { weighing, production };
}

async function fetchMaterialSummaryFromApi(year, months) {
  const weighingMap = new Map();
  const productionMap = new Map();

  const results = await Promise.all(
    months.map((month) =>
      client
        .get("/reports/material-summary/", { params: { year, month } })
        .then((res) => res.data)
        .catch(() => null)
    )
  );

  let hasData = false;
  results.forEach((data) => {
    if (!data) return;
    hasData = true;
    (data.weighing || []).forEach((row) => {
      const key = `${row.hotel}|${row.department}|${row.material}|${row.type}`;
      weighingMap.set(key, (weighingMap.get(key) || 0) + Number(row.total_kg || 0));
    });
    (data.production || []).forEach((row) => {
      const key = `${row.hotel}|${row.material}`;
      const cur = productionMap.get(key) || { qty: 0, unit: row.unit_weight_kg, kg: 0 };
      cur.qty += Number(row.total_qty || 0);
      cur.kg += Number(row.total_kg || 0);
      if (row.unit_weight_kg) cur.unit = row.unit_weight_kg;
      productionMap.set(key, cur);
    });
  });

  if (!hasData) return null;

  const weighing = [...weighingMap.entries()]
    .map(([key, total_kg]) => {
      const [hotel, department, material, type] = key.split("|");
      return { hotel, department, material, type, total_kg };
    })
    .sort((a, b) => {
      if (a.hotel !== b.hotel) return a.hotel.localeCompare(b.hotel, "tr");
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      if (a.department !== b.department) return a.department.localeCompare(b.department, "tr");
      return a.material.localeCompare(b.material, "tr");
    });

  const production = [...productionMap.entries()]
    .map(([key, item]) => {
      const [hotel, material] = key.split("|");
      return [hotel, material, item.qty || null, item.unit ?? null, item.kg];
    })
    .sort((a, b) => a[0].localeCompare(b[0], "tr") || a[1].localeCompare(b[1], "tr"));

  return { weighing, production };
}

async function fetchMaterialSummary(year, months) {
  const fromApi = await fetchMaterialSummaryFromApi(year, months);
  if (fromApi?.weighing?.length || fromApi?.production?.length) return fromApi;
  return fetchMaterialSummaryFromRecords(year, months);
}

function addWeighingSheet(workbook, sheetName, rows) {
  return addSheet(
    workbook,
    sheetName,
    ["Otel", "Departman", "Malzeme", "Toplam kg"],
    rows.map((r) => [r.hotel, r.department, r.material, r.total_kg])
  );
}

export async function downloadDashboardExcelReport({
  periodLabel,
  year,
  months,
  grand,
  hotels,
  deptChart,
  filename,
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "HelpDesk Çamaşırhane";
  workbook.created = new Date();

  addSheet(
    workbook,
    "Genel Toplam",
    ["Otel", "Kirli kg", "Temiz kg", "Üretim kg", "Kirli-Temiz", "Temiz-Üretim"],
    hotels.map((h) => [
      h.name,
      h.Kirli,
      h.Temiz,
      h["Üretim"],
      h.dirty_clean_diff_kg,
      h.clean_production_diff_kg,
    ])
  );
  const genel = workbook.getWorksheet("Genel Toplam");
  genel.addRow([
    "GENEL TOPLAM",
    grand.dirty_kg,
    grand.clean_kg,
    grand.production_kg,
    grand.dirty_clean_diff_kg,
    grand.clean_production_diff_kg,
  ]);
  genel.lastRow.font = { bold: true, color: { argb: "FFFF0000" } };

  addSheet(
    workbook,
    "Departman Özeti",
    ["Departman", "Kirli kg", "Temiz kg", "Kirli-Temiz"],
    deptChart.map((d) => [d.name, d.Kirli, d.Temiz, d.Fark])
  );

  const deptRows = [];
  hotels.forEach((h) => {
    (h.departments || []).forEach((d) => {
      deptRows.push([h.name, d.name, d.Kirli, d.Temiz, d.Fark]);
    });
  });
  addSheet(
    workbook,
    "Otel Departman",
    ["Otel", "Departman", "Kirli kg", "Temiz kg", "Kirli-Temiz"],
    deptRows
  );

  const materialData = await fetchMaterialSummary(year, months);
  if (materialData?.weighing?.length) {
    const dirtyRows = materialData.weighing.filter((r) => r.type === "DIRTY");
    const cleanRows = materialData.weighing.filter((r) => r.type === "CLEAN");
    if (dirtyRows.length) addWeighingSheet(workbook, "Tartım Kirli", dirtyRows);
    if (cleanRows.length) addWeighingSheet(workbook, "Tartım Temiz", cleanRows);
  }

  if (materialData?.production?.length) {
    addSheet(
      workbook,
      "Üretim",
      ["Otel", "Malzeme", "Toplam Adet", "Gramaj", "Toplam kg"],
      materialData.production
    );
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const safeLabel = (periodLabel || String(year)).replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    filename || `camasirhane_rapor_${safeLabel}.xlsx`
  );
}
